import React, { useState, useEffect, useCallback } from "react";
import { Eye } from "lucide-react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Loading, Empty, SectionHeader, Card, SubBadge, SupportBadge, PLAN_LABEL, fmtDate, downloadCsv, ExportButton } from "./shared";
import { SelectableTable, SelectAllHeaderCheckbox, RowCheckbox } from "@/components/admin/SelectableTable";

function OrgDetail({ orgId, onClose }) {
  const [d, setD] = useState(null);
  const [scope, setScope] = useState({ geographic_level: "", geographic_entity: "", geographic_code: "" });
  const [saving, setSaving] = useState(false);
  const [savingDemo, setSavingDemo] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (orgId) api.get(`/admin/organizations/${orgId}`).then((r) => {
      setD(r.data);
      setScope({
        geographic_level: r.data.geographic_level || "",
        geographic_entity: r.data.geographic_entity || "",
        geographic_code: r.data.geographic_code || "",
      });
    }).catch(() => {});
  }, [orgId]);
  const saveScope = async () => {
    setSaving(true); setErr("");
    try {
      await api.patch(`/admin/organizations/${orgId}/geo-scope`, {
        geographic_level: scope.geographic_level || null,
        geographic_entity: scope.geographic_entity || null,
        geographic_code: scope.geographic_code || null,
      });
      const r = await api.get(`/admin/organizations/${orgId}`);
      setD(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Échec de la mise à jour du périmètre.");
    } finally { setSaving(false); }
  };
  const setDemoStatus = async (isDemo) => {
    setSavingDemo(true); setErr("");
    try {
      await api.patch(`/admin/organizations/${orgId}/demo`, {
        is_demo: isDemo,
        demo_mode: isDemo ? "immutable" : null,
      });
      const r = await api.get(`/admin/organizations/${orgId}`);
      setD(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Échec de la mise à jour du statut Démo.");
    } finally { setSavingDemo(false); }
  };
  return (
    <Dialog open={!!orgId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="org-detail-dialog">
        <DialogHeader><DialogTitle>{d ? d.name : "Chargement…"}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Détails de l'organisation</DialogDescription>
        {!d ? <Loading /> : (
          <div className="space-y-5 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <Info label="Type" value={d.type} />
              <Info label="Pays" value={d.country || "—"} />
              <Info label="Région" value={d.region || "—"} />
              <Info label="Créée le" value={fmtDate(d.created_at)} />
              <Info label="Plan" value={PLAN_LABEL[d.subscription.plan] || d.subscription.plan} />
              <Info label="Abonnement" value={<SubBadge status={d.subscription.status} />} />
              <Info label="Support" value={<SupportBadge status={d.support.status} />} />
              <Info label="GSA" value={d.gsa_enabled ? "Activé" : "Désactivé"} />
              <Info label="Campagnes" value={d.campaign_count} />
              <Info label="Partenariats actifs" value={d.partnership_count} />
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="admin-org-demo-status">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Profil de démonstration</p>
                  <p className="mt-1 font-semibold text-[#17201A]" data-testid="admin-demo-current-status">
                    {d.is_demo ? "Démo immuable" : "Organisation standard"}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    Le mode immuable protège l'espace de démonstration contre les modifications du prospect.
                  </p>
                </div>
                {d.is_demo ? (
                  <button onClick={() => setDemoStatus(false)} disabled={savingDemo} data-testid="admin-demo-disable"
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-50">
                    {savingDemo ? "Enregistrement…" : "Désactiver la Démo"}
                  </button>
                ) : (
                  <button onClick={() => setDemoStatus(true)} disabled={savingDemo} data-testid="admin-demo-enable"
                    className="rounded-lg bg-[#17201A] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                    {savingDemo ? "Activation…" : "Activer la Démo immuable"}
                  </button>
                )}
              </div>
            </div>
            {/* Phase 3A — Périmètre géographique métier (édition platform-admin, backfill contrôlé) */}
            <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4" data-testid="admin-org-geo-scope">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Périmètre géographique</p>
              <div className="grid grid-cols-3 gap-3">
                <select value={scope.geographic_level} data-testid="admin-geo-level"
                  onChange={(e) => setScope((s) => ({ ...s, geographic_level: e.target.value }))}
                  className="h-10 rounded-lg border border-slate-200 px-2 text-sm">
                  <option value="">— Non défini —</option>
                  <option value="COUNTRY">COUNTRY</option>
                  <option value="REGION">REGION</option>
                  <option value="CITY">CITY</option>
                  <option value="DISTRICT">DISTRICT</option>
                  <option value="NEIGHBORHOOD">NEIGHBORHOOD</option>
                  <option value="STREET">STREET</option>
                  <option value="DESTINATION">DESTINATION</option>
                  <option value="PROPERTY">PROPERTY</option>
                </select>
                <input value={scope.geographic_entity} data-testid="admin-geo-entity" placeholder="Entité (ex. France)"
                  onChange={(e) => setScope((s) => ({ ...s, geographic_entity: e.target.value }))}
                  className="h-10 rounded-lg border border-slate-200 px-3 text-sm" />
                <input value={scope.geographic_code} data-testid="admin-geo-code" placeholder="Code (opt.)"
                  onChange={(e) => setScope((s) => ({ ...s, geographic_code: e.target.value }))}
                  className="h-10 rounded-lg border border-slate-200 px-3 text-sm" />
              </div>
              {err && <p className="mt-2 text-xs text-red-600" data-testid="admin-geo-error">{err}</p>}
              <button onClick={saveScope} disabled={saving} data-testid="admin-geo-save"
                className="mt-3 rounded-lg bg-[#17201A] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                {saving ? "Enregistrement…" : "Enregistrer le périmètre"}
              </button>
            </div>
            {d.support.note && <div className="rounded-lg bg-amber-50 p-3 text-amber-800"><span className="font-semibold">Note support :</span> {d.support.note}</div>}
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Membres ({d.members.length})</p>
              <div className="space-y-1">
                {d.members.map((m, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                    <span className="text-[#17201A]">{m.name} <span className="text-slate-400">· {m.email}</span></span>
                    <span className="text-xs font-semibold text-[#3E7256]">{m.role}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">Sources de données ({d.imports.length})</p>
              {d.imports.length === 0 ? <p className="text-slate-400">Aucun import.</p> : (
                <div className="space-y-1">
                  {d.imports.map((im) => (
                    <div key={im.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                      <span className="text-[#17201A]">{im.source} <span className="text-slate-400">· {im.data_type}</span></span>
                      <span className="text-xs text-slate-500">{im.row_count} lignes · {fmtDate(im.imported_at)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Info = ({ label, value }) => (
  <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</p><p className="mt-0.5 text-[#17201A]">{value}</p></div>
);

export default function AdminOrganizations() {
  const [orgs, setOrgs] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const load = useCallback(() => { api.get("/admin/organizations").then((r) => setOrgs(r.data.organizations || [])).catch(() => setOrgs([])); }, []);
  useEffect(() => { load(); }, [load]);
  if (orgs === null) return <Loading />;

  const exportCsv = () => downloadCsv(
    "organizations.csv",
    ["Organisation", "Type", "Pays", "Région", "Membres", "Imports", "Plan", "Abonnement", "Support", "GSA", "Créée le"],
    orgs.map((o) => [o.name, o.type, o.country || "", o.region || "", o.member_count, o.import_count,
      o.subscription.plan, o.subscription.status, o.support.status, o.gsa_enabled ? "oui" : "non", o.created_at || ""]),
  );

  return (
    <div data-testid="admin-organizations">
      <div className="mb-5 flex items-start justify-between gap-4">
        <SectionHeader title="Organizations" subtitle="Toutes les organisations de la plateforme (lecture seule)." />
        <ExportButton onClick={exportCsv} testid="export-organizations" />
      </div>
      {orgs.length === 0 ? <Empty label="Aucune organisation." testid="orgs-empty" /> : (
        <Card>
          <SelectableTable rows={orgs} rowId={(o) => o.id} filename="adminorganizations.csv" ariaLabel="Tableau — sélection et défilement">
            <table className="w-full min-w-[900px]">
              <thead><tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"><th className="px-3 py-2"><SelectAllHeaderCheckbox /></th>
                <th className="px-5 py-3">Organisation</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Pays</th>
                <th className="px-5 py-3">Membres</th><th className="px-5 py-3">Imports</th><th className="px-5 py-3">Plan</th>
                <th className="px-5 py-3">Abonnement</th><th className="px-5 py-3">Support</th><th className="px-5 py-3">GSA</th><th className="px-5 py-3"></th>
              </tr></thead>
              <tbody>
                {orgs.map((o) => (
                  <tr key={o.id} data-testid={`org-row-${o.id}`} className="border-b border-slate-100 last:border-0"><td className="px-3 py-2"><RowCheckbox id={o.id} /></td>
                    <td className="px-5 py-3 text-sm font-medium text-[#17201A]">{o.name}</td>
                    <td className="px-5 py-3 text-sm text-slate-600">{o.type}</td>
                    <td className="px-5 py-3 text-sm text-slate-600">{o.country || "—"}</td>
                    <td className="px-5 py-3 text-sm text-slate-600">{o.member_count}</td>
                    <td className="px-5 py-3 text-sm text-slate-600">{o.import_count}</td>
                    <td className="px-5 py-3 text-sm text-slate-600">{PLAN_LABEL[o.subscription.plan] || o.subscription.plan}</td>
                    <td className="px-5 py-3"><SubBadge status={o.subscription.status} /></td>
                    <td className="px-5 py-3"><SupportBadge status={o.support.status} /></td>
                    <td className="px-5 py-3 text-sm text-slate-600">{o.gsa_enabled ? "Oui" : "—"}</td>
                    <td className="px-5 py-3">
                      <button data-testid={`org-detail-${o.id}`} onClick={() => setDetailId(o.id)} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">
                        <Eye size={13} /> Détails
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SelectableTable>
        </Card>
      )}
      <OrgDetail orgId={detailId} onClose={() => setDetailId(null)} />
    </div>
  );
}
